import { useState, useRef, useCallback, useEffect, Fragment } from 'react';
import { Star, AlertTriangle, Flag, X, RefreshCw } from 'lucide-react';
import useCpiYoy from '@/hooks/useCpiYoy';
import { computeRealYield } from '@/lib/realYield';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAnchoredTip } from '@/components/useAnchoredTip';
import DividendChangeBadge from '@/components/DividendChangeBadge';
import PreferredCallRiskChip from '@/components/PreferredCallRiskChip';
import QualityBadge from '@/components/QualityBadge';
import CapitalScoreBadge from '@/components/CapitalScoreBadge';
import LeveragedCefIcon from '@/components/LeveragedCefIcon';
import { WarrantBadge } from '@/components/WarrantBadge';
import { RightBadge, isSpacRight } from '@/components/RightPanel';
import RevenueSparkline from '@/components/RevenueSparkline';
import WatchlistRadarChips from '@/components/WatchlistRadarChips';
import TriangleScoreChip from '@/components/TriangleScoreChip';
import { ccToFlag, ccName } from '@/lib/flagEmoji';

// Plain-English explanations shown on hover/tap of each sortable column.
// Skip "Name" per user request — label is self-explanatory.
const HEADER_TIPS = {
  price: 'Current last-trade price. Refreshes when you open a stock, add it to your portfolio or watchlist, or sign in with existing holdings — not live-ticking while browsing.',
  yield: 'Dividend Yield = annual dividend ÷ current price. Higher = more immediate income (and often more risk). 2–5% is a typical "healthy grower" band.',
  divyr: 'Annual Dividend — the total dollars per share paid out over a year. (Monthly × 12, Quarterly × 4, etc.)',
  payout: 'Payout Ratio — what % of earnings are paid out as dividends. Under 60% is comfortable; over 100% signals a potential dividend cut (REITs/BDCs exempt).',
  '1mo': 'Price change over the last 30 days. Capital appreciation only — dividends not included.',
  '6mo': 'Price change over the last 6 months. Capital appreciation only.',
  '1yr': 'Price change over the last 12 months. Capital appreciation only — dividends not included.',
  perf_1m: 'Price change over the last 30 days. Capital appreciation only — dividends not included.',
  perf_1y: 'Price change over the last 12 months. Capital appreciation only — dividends not included.',
};

// Mobile swipe hook — fires onSwipeLeft/Right only when the user is NOT
// actually scrolling the table (horizontal) or the page (vertical). This
// prevents the ambiguity on wider-than-viewport tables where a horizontal
// scroll gesture was accidentally triggering a "remove from watchlist"
// action on every row the finger passed over.
function useSwipe(onSwipeLeft, onSwipeRight) {
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const touchEndX = useRef(null);
  const touchEndY = useRef(null);
  const scrollerRef = useRef(null);         // nearest horizontally-scrollable ancestor
  const startScrollLeft = useRef(0);
  const scrollerMovedDuringTouch = useRef(false);
  const minSwipeDistance = 60;

  // Walk up from the touched element to find the first ancestor that is
  // horizontally scrollable AND currently overflowing. Uses computed style
  // so we don't mistake ancestors that happen to be scrolling vertically
  // for a horizontal scroller.
  const findHorizontalScroller = (el) => {
    let cur = (el && el.nodeType === 1) ? el : (el ? el.parentElement : null);
    while (cur) {
      const style = window.getComputedStyle(cur);
      const overflowX = style.overflowX;
      if ((overflowX === 'auto' || overflowX === 'scroll') &&
          cur.scrollWidth - cur.clientWidth > 1) {
        return cur;
      }
      cur = cur.parentElement;
    }
    return null;
  };

  const onTouchStart = useCallback((e) => {
    const t = e.targetTouches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
    touchEndX.current = null;
    touchEndY.current = null;
    scrollerRef.current = findHorizontalScroller(e.target);
    startScrollLeft.current = scrollerRef.current ? scrollerRef.current.scrollLeft : 0;
    scrollerMovedDuringTouch.current = false;
  }, []);

  const onTouchMove = useCallback((e) => {
    const t = e.targetTouches[0];
    touchEndX.current = t.clientX;
    touchEndY.current = t.clientY;
    // Sample scrollLeft continuously — the browser may scroll and then
    // reverse during the same gesture; we only need to know IF it ever
    // moved during this touch.
    if (scrollerRef.current &&
        Math.abs(scrollerRef.current.scrollLeft - startScrollLeft.current) > 2) {
      scrollerMovedDuringTouch.current = true;
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    const startX = touchStartX.current;
    const startY = touchStartY.current;
    const endX = touchEndX.current;
    const endY = touchEndY.current;
    const scroller = scrollerRef.current;
    const scrollerMoved = scrollerMovedDuringTouch.current;
    touchStartX.current = null;
    touchStartY.current = null;
    touchEndX.current = null;
    touchEndY.current = null;
    scrollerRef.current = null;
    scrollerMovedDuringTouch.current = false;

    if (startX == null || endX == null) return;
    const dx = startX - endX;
    const dy = (startY ?? 0) - (endY ?? 0);

    // Guard 1: a horizontal scroller exists in the ancestry → the user's
    // intent on this gesture is AMBIGUOUS (could be scroll, could be swipe-
    // to-remove). Disable swipe-to-remove entirely in that case; the user
    // can always tap the star to toggle. This is the safest rule because
    // swipe-to-remove is DESTRUCTIVE and scroll-gesture is the common case
    // on a wide table.
    if (scroller) return;

    // Guard 2: scroller moved during touch (defensive — redundant with
    // guard 1 but keeps the behaviour correct even if the scroller wasn't
    // detected at touchstart).
    if (scrollerMoved) return;

    // Guard 3: vertical dominated → page scroll.
    if (Math.abs(dy) > Math.abs(dx)) return;

    if (Math.abs(dx) >= minSwipeDistance) {
      if (dx > 0) onSwipeLeft?.();
      else onSwipeRight?.();
    }
  }, [onSwipeLeft, onSwipeRight]);

  return { onTouchStart, onTouchMove, onTouchEnd };
}

function PerfCell({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">--</span>;
  }
  const isPositive = value >= 0;
  return (
    <span className={`font-mono text-sm font-medium ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
      {isPositive ? '+' : ''}{value}%
    </span>
  );
}

const PAYOUT_COLORS = {
  Weekly: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  Monthly: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  Quarterly: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  'Semi-Annually': 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  Annually: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};

const TIER_CONFIG = {
  king: { icon: '\u{1F451}', label: 'Dividend King' },
  aristocrat: { icon: '\u{1F3C6}', label: 'Dividend Aristocrat' },
  achiever: { icon: '\u{1F396}\uFE0F', label: 'Dividend Achiever' },
};

function TierBadge({ tier }) {
  if (!tier) return null;
  const c = TIER_CONFIG[tier];
  if (!c) return null;
  return <span title={c.label} className="text-xs">{c.icon}</span>;
}

function PayoutBadge({ frequency }) {
  if (!frequency) return <span className="shimmer inline-block w-16 h-5 rounded" />;
  const color = PAYOUT_COLORS[frequency] || 'bg-muted text-muted-foreground';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>
      {frequency}
    </span>
  );
}

// Stale-price visuals removed (May 2026): Massive WS + daily snapshot now
// keep prices live during market hours, so the "Updated …" hint and
// gray/yellow/red AGE_COLORS no longer reflect reality. Prices render in
// the standard text color and the timestamp label is omitted.

function SortableHead({ label, sortKey, currentSort, onSort, className, hidden, tipHandlers, trailing }) {
  const isActive = currentSort.key === sortKey;
  const arrow = isActive ? (currentSort.dir === 'asc' ? ' \u25B2' : ' \u25BC') : '';
  return (
    <TableHead
      className={`font-bold text-xs uppercase tracking-wider cursor-pointer select-none hover:text-foreground transition-colors ${className || ''} ${hidden || ''}`}
      onClick={() => onSort(sortKey)}
      data-testid={`sort-${sortKey}`}
      {...(tipHandlers || {})}
    >
      {label}{arrow}{trailing}
    </TableHead>
  );
}

// Rotating loading indicator shown in the header between Name and
// Price whenever the table is fetching (page change, sort refetch,
// initial load). text-foreground = black in light mode / white in dark.
function TableLoadingSpinner() {
  return (
    <RefreshCw
      className="w-3.5 h-3.5 animate-spin text-foreground inline-block ml-2 -mt-0.5"
      data-testid="table-loading-spinner"
      aria-label="Loading stocks"
    />
  );
}

function SkeletonRow() {
  return (
    <TableRow>
      {Array.from({ length: 8 }).map((_, i) => (
        <TableCell key={i}><div className="h-4 rounded shimmer w-16" /></TableCell>
      ))}
    </TableRow>
  );
}

function SwipeableRow({ stock, inWatchlist, inPortfolio, onStockClick, onToggleWatchlist, onPerfClick, showRadarChips, trendingTickers, dividendFilter = 'dividend', user = null, yieldMode = 'nominal', cpiYoy = null, isWatchlistView = false }) {
  // Round 95z — Section 5: adaptive columns + badge gating.
  // Hide dividend-specific badges/columns when the stock is non-div
  // (prevents leakage like FLWS showing triangle_score=51 because the
  // pre-95z warmer didn't filter non-div rows). Column visibility
  // also depends on the active table filter.
  const isNonDiv = stock?.pays_dividend === false;
  const showDivBadges = !isNonDiv;
  const hideDivYr = dividendFilter !== 'dividend';
  // Round 96b (Feb 2026) — Per-user request: keep 1Mo + 6Mo columns
  // visible across all three segmented-control modes. Capital
  // appreciation is just as relevant for non-dividend stocks as for
  // dividend payers — arguably MORE so. Only Div/Yr is dividend-mode-
  // exclusive now.
  const hide1Mo6Mo = false;
  // Round 96b (Feb 2026) — Drop Yield + Payout columns in pure
  // Non-Dividend mode. They were rendering as empty "—" placeholders
  // for every row (non-div stocks have no current yield or payment
  // cadence) which wasted two columns of horizontal space. ALL mode
  // keeps them visible since it shows both div and non-div rows
  // interleaved and the dividend rows still need those cells.
  const hideYieldPayout = dividendFilter === 'non_dividend';
  // Round 96b (Feb 2026) — Sector + Industry surface only in pure
  // Non-Dividend mode (occupying the dropped Yield+Payout slots).
  // SIC-derived fields from the Massive nightly job.
  const showSectorIndustry = dividendFilter === 'non_dividend';
  // Round 96b (Feb 2026) — Market Cap and (All-mode-only) Sector
  // columns. Market Cap has the best non-div coverage of any unused
  // field (77% vs ~0% for perf), so we add it across All + Non-Div
  // modes. ALL-mode Sector mirrors the Non-Div sector column.
  const showMarketCap = dividendFilter !== 'dividend';
  const showAllSector = dividendFilter === 'all';
  const [swipeAction, setSwipeAction] = useState(null);

  const swipe = useSwipe(
    // Swipe left = remove from watchlist
    () => {
      if (inWatchlist) {
        setSwipeAction('remove');
        onToggleWatchlist(stock.ticker);
        setTimeout(() => setSwipeAction(null), 600);
      }
    },
    // Swipe right = add to watchlist
    () => {
      if (!inWatchlist) {
        setSwipeAction('add');
        onToggleWatchlist(stock.ticker);
        setTimeout(() => setSwipeAction(null), 600);
      }
    }
  );

  return (
    <TableRow
      className={`cursor-pointer group hover:bg-muted/50 transition-all ${
        swipeAction === 'remove' ? 'bg-red-500/10' : swipeAction === 'add' ? 'bg-green-500/10' : ''
      }`}
      onClick={() => onStockClick(stock.ticker)}
      data-testid={`stock-row-${stock.ticker}`}
      {...swipe}
    >
      <TableCell className="px-2 py-3 w-8">
        <button
          onClick={(e) => {
            e.stopPropagation();
            // If the user was actually swiping (moved >10px during touch),
            // suppress the star click so a horizontal scroll gesture
            // doesn't accidentally remove the stock from the watchlist.
            if (e.currentTarget.dataset.swiped === '1') {
              e.currentTarget.dataset.swiped = '';
              return;
            }
            onToggleWatchlist(stock.ticker);
          }}
          onTouchStart={(e) => {
            e.currentTarget.dataset.touchX = String(e.targetTouches[0].clientX);
            e.currentTarget.dataset.touchY = String(e.targetTouches[0].clientY);
            e.currentTarget.dataset.swiped = '';
          }}
          onTouchMove={(e) => {
            const sx = parseFloat(e.currentTarget.dataset.touchX || '0');
            const sy = parseFloat(e.currentTarget.dataset.touchY || '0');
            const dx = e.targetTouches[0].clientX - sx;
            const dy = e.targetTouches[0].clientY - sy;
            if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
              e.currentTarget.dataset.swiped = '1';
            }
          }}
          className="p-0.5"
          data-testid={`watchlist-star-${stock.ticker}`}
          title={inWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
        >
          <Star className={`w-4 h-4 transition-colors ${inWatchlist ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground/40 hover:text-muted-foreground'}`} />
        </button>
      </TableCell>
      <TableCell className="py-3">
        <div className="flex items-center gap-2">
          {inPortfolio && (
            <div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" title="In portfolio" />
          )}
          <div>
            <span className="font-semibold text-sm block group-hover:text-primary transition-colors" title={stock.name && stock.name !== stock.ticker ? stock.name : stock.ticker}>
              {(() => {
                const name = stock.name && stock.name !== stock.ticker ? stock.name : stock.ticker;
                const maxLen = 30;
                return name.length > maxLen ? name.slice(0, maxLen - 3) + ' ...' : name;
              })()}
            </span>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="font-mono text-xs text-yellow-600 dark:text-yellow-400 font-bold" data-testid={`ticker-${stock.ticker}`}>
                {stock.ticker}
              </span>
              <TierBadge tier={showDivBadges ? stock.dividend_tier : null} />
              {/* Round 96b (Feb 2026) — Non-Div Tier pill rendered
                  inline next to the ticker so non-dividend rows have
                  the same badge density as dividend rows (REIT, ADR,
                  BDC etc. cluster). Replaces the standalone "Tier"
                  column at the end of the row — visually richer and
                  frees a column slot for Sector/Industry data. */}
              {isNonDiv && stock.non_dividend_quality_tier && (
                <button
                  type="button"
                  onClick={(e) => {
                    // Round 96b (Feb 2026) — Clicking the inline tier
                    // pill jumps straight to the Growth tab where the
                    // Quality Score Breakdown card lives. Without
                    // stopPropagation, the parent row's onClick would
                    // also fire and we'd open on the default tab.
                    e.stopPropagation();
                    onStockClick(stock.ticker, 'non-div-growth');
                  }}
                  className={`inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-tight border transition-colors hover:brightness-110 cursor-pointer ${
                    stock.non_dividend_quality_tier === 'GROWTH-S' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-300/60 dark:border-emerald-700/60'
                    : stock.non_dividend_quality_tier === 'GROWTH-A' ? 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border-sky-300/60 dark:border-sky-700/60'
                    : stock.non_dividend_quality_tier === 'GROWTH-B' ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 border-blue-300/60 dark:border-blue-700/60'
                    : stock.non_dividend_quality_tier === 'GROWTH-C' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-300/60 dark:border-amber-700/60'
                    : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-300/60 dark:border-rose-700/60'
                  }`}
                  title={`Non-div quality tier · score ${stock.non_dividend_quality_score ?? '—'}/100. Click to view the per-signal breakdown in the Growth tab.`}
                  data-testid={`non-div-tier-inline-${stock.ticker}`}
                >
                  {stock.non_dividend_quality_tier.replace('GROWTH-', 'G-')}
                </button>
              )}
              {/* Feb 2026 — Dividend rows show DividendHealthQualityBadge.
                  Non-dividend rows now surface the Capital Analytics rank
                  pill instead (avoids the empty "dot + dashes" placeholder
                  the QualityBadge falls back to on non-div tickers). The
                  badge clicks straight into the Capital Analytics tab.
                  When a non-div ticker has no EQR score cached yet, both
                  components render null so the row stays clean. */}
              {isNonDiv ? (
                stock?.equity_quality?.rank != null && (
                  <CapitalScoreBadge
                    rank={stock.equity_quality.rank}
                    verdict={stock.equity_quality.verdict}
                    verdictLabel={stock.equity_quality.verdict_label}
                    size="xs"
                    ticker={stock.ticker}
                    testIdSuffix="row"
                    onClick={() => onStockClick(stock.ticker, 'capital-analytics')}
                  />
                )
              ) : (
                <QualityBadge stock={showDivBadges ? stock : { ...stock, dividend_quality_score: null, dividend_quality_tier: null }} size="xs" interactive testIdSuffix="row" />
              )}
              {/* Round 97l (Feb 2026) — Founder Stake Detector. Fires
                  when a SEC Form 3 baseline holding signals a founder-
                  level / engaged-insider stake. Computed nightly by
                  services/form3_warmer.py and stored in
                  stock_cache.form3_founder_stake. Clicking opens the
                  Stock Modal on the Insider tab.
                  Gated to admins for now since the Insider tab itself
                  is paywalled — swap this check to `user?.role ===
                  'admin' || user?.is_pro` when the non-div paywall
                  ships so paying users get the badge too. */}
              {user?.role === 'admin' && stock.form3_founder_stake?.detected && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStockClick(stock.ticker, 'insider-activity');
                  }}
                  className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border border-sky-300/60 dark:border-sky-700/60 hover:brightness-110 cursor-pointer"
                  title={(() => {
                    const fs = stock.form3_founder_stake;
                    const owner = fs.top_owner || 'Top insider';
                    const role = fs.top_role || 'Insider';
                    const sharesK = fs.top_shares
                      ? fs.top_shares >= 1_000_000
                        ? `${(fs.top_shares / 1_000_000).toFixed(1)}M`
                        : `${(fs.top_shares / 1_000).toFixed(0)}K`
                      : null;
                    const pct = fs.top_pct != null
                      ? ` (${(fs.top_pct * 100).toFixed(1)}% of float)`
                      : '';
                    return `Founder Stake Detector — ${owner} (${role}) holds ${sharesK || '—'} baseline shares${pct}. Click to view the full Initial Insider Positions table.`;
                  })()}
                  data-testid={`founder-stake-badge-${stock.ticker}`}
                >
                  <Flag className="w-2.5 h-2.5" />
                  Founder
                </button>
              )}
              {stock.is_leveraged && (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300 border border-red-300/60 dark:border-red-800/60"
                  title="Leveraged / inverse ETF — amplified risk; not suitable for long-term dividend investing"
                  data-testid={`leveraged-badge-${stock.ticker}`}
                >
                  <AlertTriangle className="w-2.5 h-2.5" />
                  Leveraged
                </span>
              )}
              {stock.uses_futures && !stock.is_leveraged && (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300/60 dark:border-amber-800/60"
                  title="Futures-based fund — uses derivatives for exposure. Carries margin/leverage risk and roll costs; returns can diverge from the underlying over time."
                  data-testid={`futures-badge-${stock.ticker}`}
                >
                  <AlertTriangle className="w-2.5 h-2.5" />
                  Futures
                </span>
              )}
              {stock.is_adr && (
                <span
                  className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-pink-100 text-red-800 dark:bg-pink-200/90 dark:text-red-900 border border-red-300/60 dark:border-red-400/60"
                  title={`American Depositary Receipt — U.S.-listed certificate representing shares of a foreign company. Trades and pays dividends in USD.${stock.country_code ? `\n\nCountry of origin: ${ccName(stock.country_code)}` : ''}`}
                  data-testid={`adr-badge-${stock.ticker}`}
                >
                  {/* Round 95q (Jun 2026) — country flag for ADR
                      tax-treaty awareness. */}
                  {stock.country_code && (
                    <span className="text-sm leading-none">{ccToFlag(stock.country_code)}</span>
                  )}
                  ADR
                </span>
              )}
              {stock.is_bdc && (
                <span
                  className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300 border border-indigo-300/60 dark:border-indigo-700/60"
                  title="Business Development Company — closed-end fund that loans to small/mid-sized private businesses. By law, distributes 90%+ of taxable income; yields of 8-12% are normal."
                  data-testid={`bdc-badge-${stock.ticker}`}
                >
                  BDC
                </span>
              )}
              {stock.is_spac && (
                <span
                  className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-slate-200 text-slate-800 dark:bg-slate-800/70 dark:text-slate-200 border border-slate-400/60 dark:border-slate-600/60"
                  title="Blank-Check / SPAC — Special Purpose Acquisition Company. A cash shell listed to acquire a private business. Pays NO dividends. Ticker often ends in U/UN (a 'unit' = share + warrant)."
                  data-testid={`spac-badge-${stock.ticker}`}
                >
                  SPAC
                </span>
              )}
              {stock.is_cef && (
                <span
                  className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-300 border border-teal-300/60 dark:border-teal-700/60"
                  title="Closed-End Fund — exchange-traded fund with a fixed share count that can trade at a premium or discount to NAV. Often uses leverage; many distribute monthly with high yields."
                  data-testid={`cef-badge-${stock.ticker}`}
                >
                  CEF
                </span>
              )}
              <LeveragedCefIcon
                ticker={stock.ticker}
                isCef={!!stock.is_cef}
                testIdPrefix="lev-cef-badge"
              />
              {/* Round P140·5 (Feb 2026) — Warrant badge in row context.
                  Reflects the redemption status (active / soon /
                  redeemed) so users spot warrant holdings + fresh
                  8-K redemption alerts without opening the modal.
                  Backed by `is_warrant` + `warrant_status` fields
                  stamped by the warrants_ingest pipeline. */}
              {!stock.is_warrant && isSpacRight(stock) && <RightBadge testIdSuffix={`row-${stock.ticker}`} />}
              {stock.is_warrant && (
                <WarrantBadge
                  status={stock.warrant_status || 'active'}
                  deadline={stock.warrant_redemption_deadline}
                  price={stock.warrant_redemption_price}
                  isUnit={!!stock.is_unit}
                  testIdSuffix={`row-${stock.ticker}`}
                />
              )}
              {stock.is_reit && (
                <span
                  className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300/60 dark:border-emerald-700/60"
                  title="Real Estate Investment Trust — company that owns, operates, or finances income-producing real estate. Required by law to distribute 90%+ of taxable income, so yields of 4-8% are typical."
                  data-testid={`reit-badge-${stock.ticker}`}
                >
                  REIT
                </span>
              )}
              <DividendChangeBadge stock={showDivBadges ? stock : null} size="sm" />
              <PreferredCallRiskChip stock={stock} onOpenTicker={(t) => onStockClick?.(t, 'insights')} />
              <TriangleScoreChip stock={showDivBadges ? stock : null} />
              {showRadarChips && <WatchlistRadarChips stock={stock} trendingTickers={trendingTickers} />}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell className="text-right font-mono text-sm">
        {stock.price ? (
          <span>
            ${stock.price.toFixed(2)}
          </span>
        ) : <span className="shimmer inline-block w-14 h-4 rounded" />}
      </TableCell>
      {/* Round 96b (Feb 2026) — Market Cap. Highest non-div coverage
          of any unused field (77% vs 47% for dividend). Renders in
          ALL mode and Non-Dividend mode (skipped in Dividend mode so
          the existing layout stays untouched). Auto-scales suffix to
          K/M/B/T. */}
      {showMarketCap && (
        <TableCell className="text-right font-mono text-sm " data-testid={`mcap-${stock.ticker}`}>
          {stock.market_cap != null ? (() => {
            const v = stock.market_cap;
            const abs = Math.abs(v);
            if (abs >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
            if (abs >= 1e9)  return `$${(v / 1e9).toFixed(2)}B`;
            if (abs >= 1e6)  return `$${(v / 1e6).toFixed(1)}M`;
            if (abs >= 1e3)  return `$${(v / 1e3).toFixed(0)}K`;
            return `$${v.toFixed(0)}`;
          })() : <span className="text-muted-foreground/50">—</span>}
        </TableCell>
      )}
      {!hideYieldPayout && (
        <TableCell className="text-right">
          {isNonDiv ? (
            <span className="font-mono text-sm text-muted-foreground/50">—</span>
          ) : stock.dividend_yield !== undefined && stock.dividend_yield !== null && stock.dividend_yield > 0 ? (() => {
            // Round 96n (Jul 3 2026) — Real vs Nominal Yield toggle.
            // "Real" = nominal yield minus latest CPI YoY. Negative
            // real yields render red (income losing purchasing power),
            // positive stays green. Cell tooltip explains the math.
            const nominal = stock.dividend_yield;
            const useReal = yieldMode === 'real' && typeof cpiYoy === 'number';
            const real = useReal ? computeRealYield(nominal, cpiYoy) : null;
            const displayed = useReal ? real : nominal;
            const isNegative = useReal && real < 0;
            const cls = isNegative
              ? 'text-red-600 dark:text-red-400'
              : 'text-green-600 dark:text-green-400';
            const tooltip = useReal
              ? `Real Yield = Nominal ${nominal.toFixed(2)}% − CPI ${cpiYoy.toFixed(2)}% = ${real.toFixed(2)}%`
              : `Nominal dividend yield. Toggle "Real" above the table to subtract current CPI YoY (${typeof cpiYoy === 'number' ? cpiYoy.toFixed(2) + '%' : 'loading…'}).`;
            return (
              <span
                className={`font-mono text-sm font-medium ${cls}`}
                title={tooltip}
                data-testid={`yield-${stock.ticker}`}
              >
                {displayed.toFixed(2)}%
              </span>
            );
          })() : stock.dividend_yield === undefined ? (
            <span className="shimmer inline-block w-12 h-4 rounded" />
          ) : (
            <span className="font-mono text-sm text-muted-foreground/50">—</span>
          )}
        </TableCell>
      )}
      {!hideDivYr && (
        <TableCell className="text-right font-mono text-sm">
          {isNonDiv ? <span className="text-muted-foreground/50">—</span>
            : stock.dividend_rate !== undefined && stock.dividend_rate !== null && stock.dividend_rate > 0 ? `$${stock.dividend_rate.toFixed(2)}`
            : stock.dividend_rate === undefined ? <span className="shimmer inline-block w-12 h-4 rounded" />
            : <span className="text-muted-foreground/50">—</span>}
        </TableCell>
      )}
      {/* Round P143 (Feb 2026) — Ex-Div column, watchlist-only. Sits
          between Div/Yr and Payout so dividend rows show the next
          ex-dividend date without cluttering the main /stocks table. */}
      {isWatchlistView && (
        <TableCell className="text-center font-mono text-xs" data-testid={`exdiv-${stock.ticker}`}>
          {isNonDiv ? <span className="text-muted-foreground/50">—</span>
            : (stock.next_ex_date || stock.next_ex_dividend_date) ? (
              <span className="text-foreground">{stock.next_ex_date || stock.next_ex_dividend_date}</span>
            ) : <span className="text-muted-foreground/50">—</span>}
        </TableCell>
      )}
      {!hideYieldPayout && (
        <TableCell className="text-center">
          {isNonDiv ? <span className="font-mono text-sm text-muted-foreground/50">—</span>
            : (stock.dividend_yield != null && stock.dividend_yield > 0) || (stock.dividend_rate != null && stock.dividend_rate > 0) ? <PayoutBadge frequency={stock.frequency} />
            : <span className="font-mono text-sm text-muted-foreground/50">—</span>}
        </TableCell>
      )}
      {/* Round 96b (Feb 2026) — Sector column in ALL mode. Non-Div
          mode already shows Sector immediately after Yield/Payout (see
          showSectorIndustry block below). Dividend mode unchanged. */}
      {showAllSector && (
        <TableCell className="text-left text-xs  pr-3 max-w-[140px] truncate" title={stock.sector || ''}>
          {stock.sector ? (
            <span className="font-medium text-foreground/90">{stock.sector}</span>
          ) : (
            <span className="text-muted-foreground/50">—</span>
          )}
        </TableCell>
      )}
      {/* Round 96b (Feb 2026) — Sector + Industry columns occupy the
          Yield + Payout slots in pure Non-Dividend mode. Both pull
          directly from the SIC-derived fields populated by the
          Massive nightly job (`sector` is the broad 2-digit grouping;
          `industry` is the verbatim sic_description). Hidden in
          Dividend / All modes since those layouts already use the
          slots for dividend data. */}
      {showSectorIndustry && (
        <TableCell className="text-left text-xs  pr-3 max-w-[140px] truncate" title={stock.sector || ''}>
          {stock.sector ? (
            <span className="font-medium text-foreground/90">{stock.sector}</span>
          ) : (
            <span className="text-muted-foreground/50">—</span>
          )}
        </TableCell>
      )}
      {showSectorIndustry && (
        <TableCell className="text-left text-xs  pr-3 max-w-[180px] truncate" title={stock.industry || ''}>
          {stock.industry ? (
            <span className="text-foreground/80">
              {(() => {
                // Trim verbatim SIC titles — they're SHOUTY ALL-CAPS
                // and sometimes 60+ chars. Title-case + truncate to
                // 24 chars so the column stays scannable.
                const raw = stock.industry.toLowerCase();
                const titled = raw.charAt(0).toUpperCase() + raw.slice(1);
                return titled.length > 24 ? titled.slice(0, 21) + '…' : titled;
              })()}
            </span>
          ) : (
            <span className="text-muted-foreground/50">—</span>
          )}
        </TableCell>
      )}
      {/* Round 97c (Feb 27, 2026) — Rev Growth column. Sparkline +
          3yr CAGR % in a single cell. Sortable via the `rev_cagr`
          backend key. Visible only in pure Non-Dividend mode so the
          dividend / all layouts stay untouched. Rows without data
          render a "—" placeholder (and are pushed to the bottom by
          the backend secondary sort regardless of direction). */}
      {showSectorIndustry && (
        <TableCell className="text-right pr-3 " data-testid={`rev-growth-cell-${stock.ticker}`}>
          {(() => {
            const cagr = stock.non_dividend_quality_signals?.revenue_3yr_cagr;
            const history = stock.non_div_revenue_history_7y;
            const hasHistory = Array.isArray(history) && history.length >= 3;
            const hasCagr = cagr != null;
            if (!hasHistory && !hasCagr) {
              return <span className="text-muted-foreground/50 text-xs">—</span>;
            }
            const pctTxt = hasCagr
              ? `${cagr >= 0 ? '+' : ''}${(cagr * 100).toFixed(1)}%`
              : '';
            const color = hasCagr
              ? cagr >= 0.10 ? 'text-emerald-600 dark:text-emerald-400'
              : cagr <= -0.05 ? 'text-rose-600 dark:text-rose-400'
              : 'text-foreground/70'
              : 'text-foreground/70';
            return (
              <div className="inline-flex items-center gap-1.5 justify-end">
                {hasHistory && (
                  <RevenueSparkline
                    history={history}
                    ticker={stock.ticker}
                    onClick={() => onStockClick(stock.ticker, 'non-div-growth')}
                  />
                )}
                {hasCagr && (
                  <span className={`font-mono text-xs font-semibold ${color}`}>
                    {pctTxt}
                  </span>
                )}
              </div>
            );
          })()}
        </TableCell>
      )}
      {!hide1Mo6Mo && (
        <TableCell className="text-right ">
          <span onClick={(e) => { e.stopPropagation(); onPerfClick?.(stock.ticker, isWatchlistView ? '3mo' : '1mo'); }} className="cursor-pointer hover:underline">
            <PerfCell value={isWatchlistView ? stock.perf_3m : stock.perf_1m} />
          </span>
        </TableCell>
      )}
      {!hide1Mo6Mo && !isWatchlistView && (
        <TableCell className="text-right ">
          <span onClick={(e) => { e.stopPropagation(); onPerfClick?.(stock.ticker, '6mo'); }} className="cursor-pointer hover:underline">
            <PerfCell value={stock.perf_6m} />
          </span>
        </TableCell>
      )}
      <TableCell className="text-right ">
        <span onClick={(e) => { e.stopPropagation(); onPerfClick?.(stock.ticker, '1y'); }} className="cursor-pointer hover:underline">
          <PerfCell value={stock.perf_1y} />
        </span>
      </TableCell>
    </TableRow>
  );
}

export default function StockTable({ stocks, loading, onStockClick, onPerfClick, watchlist, ownedTickers, onToggleWatchlist, user, sortKey, sortDir, onSort, emptyMessage, interleaveAfterRow, showRadarChips, trendingTickers, dividendFilter = 'dividend', onClearSort, isSortDefault, isWatchlistView = false, allowSpacsWarrants = null, onAllowSpacsWarrantsChange = null, allowMmf = null, onAllowMmfChange = null }) {
  const { handlers: tipFor, Tooltip: TableHeadTooltip } = useAnchoredTip();
  // Round 96n (Jul 3 2026) — Real vs Nominal Yield toggle. Persists
  // in localStorage so the user's choice sticks across pages. CPI YoY
  // is pulled from `/api/macro` (Massive Fed API, cached daily).
  const [yieldMode, setYieldMode] = useState(() => {
    try { return localStorage.getItem('dividrip.yieldMode') || 'nominal'; } catch { return 'nominal'; }
  });
  const cpiYoy = useCpiYoy();
  useEffect(() => {
    try { localStorage.setItem('dividrip.yieldMode', yieldMode); } catch { /* ignore */ }
  }, [yieldMode]);
  // If we flipped to Real but the CPI value never loaded, silently
  // fall back to Nominal for display purposes (SwipeableRow already
  // guards this, but header label + toggle state stay honest).
  const effectiveYieldMode = (yieldMode === 'real' && typeof cpiYoy !== 'number') ? 'nominal' : yieldMode;
  // Round 95z — Section 5: column visibility per dividend filter.
  // Default "dividend" preserves the original 7-column layout. "All"
  // and "non_dividend" collapse the dividend-specific cols (Div/Yr,
  // 1Mo, 6Mo) since they're meaningless or empty for non-div rows.
  const hideDivYr = dividendFilter !== 'dividend';
  // Round 96b (Feb 2026) — Matches the row-level decision above. 1Mo
  // and 6Mo always visible; only Div/Yr swaps out for non-dividend
  // and "all" modes.
  const hide1Mo6Mo = false;
  // Round 96b (Feb 2026) — Yield + Payout hidden in pure Non-Dividend
  // mode (both columns rendered as "—" for every row, wasting space).
  // ALL mode keeps them because the dividend rows mixed into the
  // result set still need those cells.
  const hideYieldPayout = dividendFilter === 'non_dividend';
  // Round 96b (Feb 2026) — Mirror of SwipeableRow flag. Sector +
  // Industry columns render only in pure Non-Dividend mode.
  const showSectorIndustry = dividendFilter === 'non_dividend';
  // Round 96b (Feb 2026) — Market Cap renders in All + Non-Div modes;
  // All-mode also gets a Sector column to match the Non-Div layout.
  const showMarketCap = dividendFilter !== 'dividend';
  const showAllSector = dividendFilter === 'all';

  const handleSort = (key) => {
    onSort?.(key);
  };

  // Map sort key names for backend compatibility
  // Round 97a (Feb 2026) — added market_cap (was missing → header
  // click defaulted to name sort silently).
  const sortKeyMap = { 'name': 'name', 'ticker': 'name', 'price': 'price', 'yield': 'yield', 'divyr': 'divyr', 'payout': 'payout', '1mo': 'perf_1m', '3mo': 'perf_3m', '6mo': 'perf_6m', '1yr': 'perf_1y', 'exdiv': 'next_ex_dividend_date', 'sector': 'sector', 'industry': 'industry', 'market_cap': 'market_cap', 'rev_cagr': 'rev_cagr' };
  const activeKey = sortKey;
  const activeDir = sortDir;

  // No client-side sort — backend handles it across all pages
  const sortedStocks = stocks || [];

  if (loading && stocks.length === 0) {
    return (
      <div className="rounded-lg border dark:border-white/[0.06] overflow-x-auto md:overflow-hidden" data-testid="stock-table-loading">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="font-bold text-xs uppercase tracking-wider w-8"></TableHead>
              <TableHead className="font-bold text-xs uppercase tracking-wider">Name<TableLoadingSpinner /></TableHead>
              <TableHead className="font-bold text-xs uppercase tracking-wider text-right">Price</TableHead>
              <TableHead className="font-bold text-xs uppercase tracking-wider text-right">Yield</TableHead>
              <TableHead className="font-bold text-xs uppercase tracking-wider text-right">Div/Yr</TableHead>
              <TableHead className="font-bold text-xs uppercase tracking-wider text-center">Payout</TableHead>
              <TableHead className="font-bold text-xs uppercase tracking-wider text-right">1 Mo</TableHead>
              <TableHead className="font-bold text-xs uppercase tracking-wider text-right">6 Mo</TableHead>
              <TableHead className="font-bold text-xs uppercase tracking-wider text-right">1 Yr</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}</TableBody>
        </Table>
      </div>
    );
  }

  if (!stocks || stocks.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground px-4" data-testid="no-stocks">
        <p className="text-lg font-semibold">{emptyMessage?.title || 'No stocks found'}</p>
        <p className="text-sm mt-1 max-w-md mx-auto font-semibold">{emptyMessage?.hint || 'Try adjusting your search query'}</p>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border dark:border-white/[0.06] overflow-x-auto md:overflow-hidden"
      data-testid="stock-table"
    >
      <TableHeadTooltip />
      {/* Round 96n (Jul 3 2026) — Real vs Nominal Yield toggle.
          Sits above the table header, right-aligned. Tiny two-state
          pill so it doesn't crowd the mobile view. Hidden entirely
          when the Yield column itself is hidden (pure Non-Dividend
          mode). Disabled state when CPI YoY hasn't loaded yet.
          Round P144 (Feb 2026) — The row ALSO renders when the
          "Allow SPAC & Warrant" checkbox is available (Non-Dividend
          mode has no yield toggle but still needs the checkbox on
          the same bar). */}
      {(!hideYieldPayout || (onAllowSpacsWarrantsChange && !isWatchlistView && dividendFilter !== 'dividend')) && (
        <div className="flex flex-wrap items-center justify-between px-2 sm:px-3 py-1.5 border-b border-border/40 dark:border-white/[0.04] bg-muted/20 gap-1.5" data-testid="yield-mode-toggle">
          {/* Feb 2026 — Mobile-only Clear Sort on the far LEFT of the
              yield-mode row (Watchlist view). Renders only when the
              parent passes `onClearSort` (Dashboard's watchlist mobile
              flow); other callers get an invisible spacer so the
              yield-mode toggle stays right-aligned. */}
          {onClearSort ? (
            <button
              type="button"
              onClick={onClearSort}
              disabled={!!isSortDefault}
              className="sm:hidden inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold transition-colors bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="clear-sort-btn-mobile-inline"
              title="Reset the column sort back to its default (name, ascending)"
            >
              <X className="w-3 h-3" />
              Clear Sort
            </button>
          ) : (
            <span className="sm:hidden" aria-hidden="true" />
          )}
          {/* Round P144 (Feb 2026) — "Allow SPAC & Warrant" checkbox.
              Sits far LEFT of the yield-mode row when the caller
              provides the toggle. Hidden entirely on Watchlist /
              Portfolio views because those tables always show every
              holding regardless of the checkbox. Also hidden in
              pure Dividend mode — SPACs and warrants never pay
              dividends so the checkbox has nothing to hide/show.
              Session-scoped persistence is owned by the parent
              (Dashboard / Screener). */}
          {onAllowSpacsWarrantsChange && !isWatchlistView && dividendFilter !== 'dividend' && (
            <label
              className="inline-flex items-center gap-1.5 cursor-pointer select-none mr-auto"
              data-testid="allow-spacs-warrants-toggle"
              title="Show SPACs (blank-check companies) and Warrants in this table. Off by default so a low-to-high price sort surfaces real stocks instead of a wall of $0.00 SPAC units. Off does NOT affect your Watchlist or Portfolio."
            >
              <input
                type="checkbox"
                checked={!!allowSpacsWarrants}
                onChange={(e) => onAllowSpacsWarrantsChange(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-border/60 cursor-pointer accent-primary"
                data-testid="allow-spacs-warrants-checkbox"
              />
              {/* Round P144·1 — Compact label on mobile (≤639 px) so the
                  checkbox + yield-mode pill both fit on one row without
                  wrapping. Full label re-appears from sm: upward. */}
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground hidden sm:inline">
                Allow: SPAC &amp; Warrant
              </span>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground sm:hidden">
                SPAC/Warrant
              </span>
            </label>
          )}
          {/* Sep 2026 — "Allow: Money Market Funds" checkbox. Dividend +
              All modes only (MMFs always pay). In All mode it sits on a
              2nd line under the SPAC & Warrant checkbox (basis-full);
              in Dividend mode it takes the SPAC checkbox's left slot. */}
          {onAllowMmfChange && !isWatchlistView && dividendFilter !== 'non_dividend' && (
            <label
              className={`inline-flex items-center gap-1.5 cursor-pointer select-none mr-auto ${dividendFilter === 'all' ? 'basis-full order-last' : ''}`}
              data-testid="allow-mmf-toggle"
              title="Show Money Market Funds (SPAXX, VMFXX…) and open-end mutual funds (VTSAX, FXAIX…) in this table. Off by default: MMFs have no price change, market cap or payout ratio, so a lowest-first sort on those columns surfaced a wall of “—” rows. Off does NOT affect your Watchlist or Portfolio."
            >
              <input
                type="checkbox"
                checked={!!allowMmf}
                onChange={(e) => onAllowMmfChange(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-border/60 cursor-pointer accent-primary"
                data-testid="allow-mmf-checkbox"
              />
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground hidden sm:inline">
                Allow: Money Market &amp; Mutual Funds
              </span>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground sm:hidden">
                MMF/Mutual
              </span>
            </label>
          )}
          <div className="flex items-center gap-1.5 ml-auto">
            {!hideYieldPayout && (
              <>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground hidden sm:inline">Yield:</span>
          <div className="flex items-center bg-background rounded-md p-0.5 border border-border/60" role="tablist" aria-label="Yield display mode">
            <button
              type="button"
              onClick={() => setYieldMode('nominal')}
              className={`px-2 py-0.5 text-[11px] font-semibold rounded transition-colors ${effectiveYieldMode === 'nominal' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              title="Show nominal dividend yield — the raw annual dividend ÷ price percentage."
              data-testid="yield-mode-nominal"
              role="tab"
              aria-selected={effectiveYieldMode === 'nominal'}
            >
              Nominal
            </button>
            <button
              type="button"
              onClick={() => setYieldMode('real')}
              disabled={typeof cpiYoy !== 'number'}
              className={`px-2 py-0.5 text-[11px] font-semibold rounded transition-colors ${effectiveYieldMode === 'real' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'} disabled:opacity-40 disabled:cursor-not-allowed`}
              title={
                typeof cpiYoy === 'number'
                  ? `Real Yield = Nominal − CPI YoY (${cpiYoy.toFixed(2)}%). Negative real yields mean your income is losing purchasing power to inflation.`
                  : 'Real Yield unavailable — CPI YoY data still loading. Retry in a moment.'
              }
              data-testid="yield-mode-real"
              role="tab"
              aria-selected={effectiveYieldMode === 'real'}
            >
              Real{typeof cpiYoy === 'number' ? ` (−${cpiYoy.toFixed(1)}%)` : ''}
            </button>
          </div>
              </>
            )}
          </div>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50 dark:[&]:border-b-white/[0.06]">
            <TableHead className="w-8 px-2"></TableHead>
            <SortableHead label="Name" sortKey="name" currentSort={{key: activeKey, dir: activeDir}} onSort={handleSort} className="min-w-[160px]" trailing={loading ? <TableLoadingSpinner /> : null} />
            <SortableHead label="Price" sortKey="price" currentSort={{key: activeKey, dir: activeDir}} onSort={handleSort} className="text-right" tipHandlers={tipFor(HEADER_TIPS.price)} />
            {showMarketCap && (
              <SortableHead label="Mkt Cap" sortKey="market_cap" currentSort={{key: activeKey, dir: activeDir}} onSort={handleSort} className="text-right" hidden="" />
            )}
            {!hideYieldPayout && (
              <SortableHead
                label={effectiveYieldMode === 'real' ? 'Real Yield' : 'Yield'}
                sortKey="yield"
                currentSort={{key: activeKey, dir: activeDir}}
                onSort={handleSort}
                className="text-right"
                tipHandlers={tipFor(
                  effectiveYieldMode === 'real' && typeof cpiYoy === 'number'
                    ? `Real Yield (Fisher-exact) = (1 + nominal yield) / (1 + CPI YoY ${cpiYoy.toFixed(2)}%) − 1. Shows whether each stock's income actually beats inflation. Negative = losing purchasing power.`
                    : HEADER_TIPS.yield
                )}
              />
            )}
            {!hideDivYr && (
              <SortableHead label="Div/Yr" sortKey="divyr" currentSort={{key: activeKey, dir: activeDir}} onSort={handleSort} className="text-right" tipHandlers={tipFor(HEADER_TIPS.divyr)} />
            )}
            {/* Round P143 (Feb 2026) — Watchlist-only Ex-Div column.
                Sortable — asc = soonest ex-dividend first, desc =
                furthest first. Nulls (non-dividend rows) sort to the
                bottom either way. */}
            {isWatchlistView && (
              <SortableHead label="Ex-Div" sortKey="exdiv" currentSort={{key: activeKey, dir: activeDir}} onSort={handleSort} className="text-center" tipHandlers={tipFor('Next ex-dividend date — the last day you must own the stock to receive the upcoming dividend. Sort ascending for soonest, descending for furthest away.')} />
            )}
            {!hideYieldPayout && (
              <SortableHead label="Payout" sortKey="payout" currentSort={{key: activeKey, dir: activeDir}} onSort={handleSort} className="text-center" tipHandlers={tipFor(HEADER_TIPS.payout)} />
            )}
            {showAllSector && (
              <SortableHead label="Sector" sortKey="sector" currentSort={{key: activeKey, dir: activeDir}} onSort={handleSort} className="text-left" hidden="" />
            )}
            {/* Round 96b (Feb 2026) — Sector + Industry headers
                replace the dropped Yield+Payout slots in pure
                Non-Dividend mode. */}
            {showSectorIndustry && (
              <SortableHead label="Sector" sortKey="sector" currentSort={{key: activeKey, dir: activeDir}} onSort={handleSort} className="text-left" hidden="" />
            )}
            {showSectorIndustry && (
              <SortableHead label="Industry" sortKey="industry" currentSort={{key: activeKey, dir: activeDir}} onSort={handleSort} className="text-left" hidden="" />
            )}
            {/* Round 97c (Feb 27, 2026) — Sortable "Rev Growth" header
                for the Non-Dividend sparkline column. Backend sorts by
                `non_dividend_quality_signals.revenue_3yr_cagr` (the
                fraction stored by the Score Non-Div Quality cron). */}
            {showSectorIndustry && (
              <SortableHead label="Rev Growth" sortKey="rev_cagr" currentSort={{key: activeKey, dir: activeDir}} onSort={handleSort} className="text-right" hidden="" tipHandlers={tipFor('Revenue 3-year CAGR. Compound annual growth rate of the most recent annual revenue vs three years ago. Sourced from Massive annual income statements via the nightly Score Non-Div Quality job. Sort to find the fastest-growing non-dividend names.')} />
            )}
            {!hide1Mo6Mo && (
              <SortableHead label={isWatchlistView ? '3 Mo' : '1 Mo'} sortKey={isWatchlistView ? '3mo' : '1mo'} currentSort={{key: activeKey, dir: activeDir}} onSort={handleSort} className="text-right" hidden="" tipHandlers={tipFor(isWatchlistView ? 'Price change over the last 3 months. Capital appreciation only — dividends not included.' : HEADER_TIPS['1mo'])} />
            )}
            {!hide1Mo6Mo && !isWatchlistView && (
              <SortableHead label="6 Mo" sortKey="6mo" currentSort={{key: activeKey, dir: activeDir}} onSort={handleSort} className="text-right" hidden="" tipHandlers={tipFor(HEADER_TIPS['6mo'])} />
            )}
            <SortableHead label="1 Yr" sortKey="1yr" currentSort={{key: activeKey, dir: activeDir}} onSort={handleSort} className="text-right" hidden="" tipHandlers={tipFor(HEADER_TIPS['1yr'])} />
            {/* Round 96b (Feb 2026) — Standalone Tier column dropped
                entirely. Non-Dividend Tier pill now renders inline
                next to the ticker on every non-div row (alongside the
                ETF/REIT/ADR/Leveraged badges the dividend rows show),
                so the dedicated column is redundant in both All and
                Non-Dividend modes. */}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedStocks.map((stock) => {
            const inWatchlist = watchlist.includes(stock.ticker);
            const inPortfolio = ownedTickers.includes(stock.ticker);
            return (
              <Fragment key={stock.ticker}>
                <SwipeableRow
                  stock={stock}
                  inWatchlist={inWatchlist}
                  inPortfolio={inPortfolio}
                  onStockClick={onStockClick}
                  onToggleWatchlist={onToggleWatchlist}
                  onPerfClick={onPerfClick}
                  showRadarChips={showRadarChips}
                  trendingTickers={trendingTickers}
                  dividendFilter={dividendFilter}
                  user={user}
                  yieldMode={effectiveYieldMode}
                  cpiYoy={cpiYoy}
                  isWatchlistView={isWatchlistView}
                />
                {/* Optional interleaved row — used by the Watchlist
                    News toggle to render each ticker's recent news
                    immediately under its row. colSpan covers all 9
                    columns so the news block spans the full table
                    width without breaking the column grid. */}
                {interleaveAfterRow && (
                  <TableRow className="hover:bg-transparent border-0">
                    <TableCell colSpan={9} className="p-0">
                      {interleaveAfterRow(stock.ticker)}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
